import { useState } from 'react';
import {
  dashboardMetricCards,
  dashboardQuickActions,
  dashboardRecentActivity,
  dashboardTasks,
  dashboardRules,
  demoRadio,
} from '@/mocks/rfwDashboardSimple';

type SidebarView = 'todo' | 'metricas' | 'actividad' | 'tareas' | 'reglas';

export default function DashboardSimplePage() {
  const [sidebarView, setSidebarView] = useState<SidebarView>('todo');

  const taskCounts = {
    alta: dashboardTasks.filter((t) => !t.completed && t.priority === 'alta').length,
    media: dashboardTasks.filter((t) => !t.completed && t.priority === 'media').length,
    baja: dashboardTasks.filter((t) => !t.completed && t.priority === 'baja').length,
    total: dashboardTasks.filter((t) => !t.completed).length,
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* ─── TOP BAR ─── */}
      <div className="bg-background-50 border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-2 text-foreground-500 hover:text-foreground-700 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              <span className="text-sm font-medium">Volver</span>
            </a>
            <span className="text-foreground-300 text-sm">|</span>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold text-xs">
                {demoRadio.frequency}
              </div>
              <div>
                <span className="text-sm font-semibold text-foreground-950">{demoRadio.name}</span>
                <span className="text-[10px] text-foreground-400 ml-2">{demoRadio.plan}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              En vivo · {demoRadio.streamListeners.toLocaleString()}
            </span>
            <a
              href="/admin/settings"
              className="w-8 h-8 rounded-lg bg-background-100 border border-background-200/70 flex items-center justify-center hover:bg-background-200/70 transition-colors cursor-pointer"
            >
              <i className="ri-settings-3-line text-foreground-500 text-sm"></i>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* ─── SIDEBAR ─── */}
          <div className="lg:w-60 shrink-0">
            <div className="bg-background-100 rounded-xl border border-background-200/70 p-3 lg:sticky lg:top-4">
              <div className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-2">
                Vistas rápidas
              </div>

              {([
                { key: 'todo', icon: 'ri-dashboard-line', label: 'Vista Completa' },
                { key: 'metricas', icon: 'ri-bar-chart-2-line', label: 'Métricas' },
                { key: 'actividad', icon: 'ri-history-line', label: 'Actividad' },
                { key: 'tareas', icon: 'ri-task-line', label: 'Tareas', badge: taskCounts.total > 0 ? taskCounts.total.toString() : undefined },
                { key: 'reglas', icon: 'ri-scales-line', label: 'Reglas' },
              ] as { key: SidebarView; icon: string; label: string; badge?: string }[]).map((item) => (
                <button
                  key={item.key}
                  onClick={() => setSidebarView(item.key)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm mb-1 transition-all cursor-pointer ${
                    sidebarView === item.key
                      ? 'bg-emerald-500 text-white font-semibold'
                      : 'text-foreground-600 hover:bg-background-200/50'
                  }`}
                >
                  <i className={`${item.icon} text-sm`}></i>
                  <span>{item.label}</span>
                  {item.badge && (
                    <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                      sidebarView === item.key
                        ? 'bg-white/20 text-white'
                        : 'bg-rose-100 text-rose-600'
                    }`}>
                      {item.badge}
                    </span>
                  )}
                </button>
              ))}

              <div className="border-t border-background-200/70 my-3"></div>
              <div className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-2">
                Acciones rápidas
              </div>

              {dashboardQuickActions.slice(0, 3).map((action) => (
                <a
                  key={action.id}
                  href={action.link}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-foreground-600 hover:bg-background-200/50 transition-colors cursor-pointer mb-1"
                >
                  <i className={`${action.icon} text-sm`}></i>
                  <span>{action.label}</span>
                </a>
              ))}
            </div>
          </div>

          {/* ─── MAIN CONTENT ─── */}
          <div className="flex-1 min-w-0">
            {/* Hero / Header del dashboard */}
            {(sidebarView === 'todo') && (
              <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-amber-50 rounded-2xl p-6 mb-6 border border-emerald-200/50">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">DASHBOARD</span>
                      <span className="text-xs text-foreground-400">Día {demoRadio.daysActive}</span>
                    </div>
                    <h1 className="text-xl md:text-2xl font-bold text-foreground-950">
                      Hola, {demoRadio.name.split(' ').pop()}
                    </h1>
                    <p className="text-sm text-foreground-500 mt-1">
                      {taskCounts.alta > 0
                        ? `Tenés ${taskCounts.alta} tarea${taskCounts.alta > 1 ? 's' : ''} urgente${taskCounts.alta > 1 ? 's' : ''}. `
                        : 'Todo en orden. '}
                      {demoRadio.streamStatus === 'online'
                        ? `Estás al aire con ${demoRadio.streamListeners.toLocaleString()} oyentes.`
                        : 'No estás transmitiendo ahora.'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-400">Próximo cobro: {demoRadio.nextBilling}</span>
                  </div>
                </div>
              </div>
            )}

            {/* ─── SECCIÓN: MÉTRICAS ─── */}
            {(sidebarView === 'todo' || sidebarView === 'metricas') && (
              <div className="mb-6">
                {sidebarView === 'metricas' && (
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-bar-chart-2-line text-emerald-600 text-lg"></i>
                    <h2 className="text-lg font-bold text-foreground-950">Métricas</h2>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {dashboardMetricCards.map((card) => (
                    <a
                      key={card.id}
                      href={card.link}
                      className={`${card.bgColor} rounded-xl p-4 border border-background-200/70 hover:border-emerald-200 transition-all cursor-pointer group`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`w-9 h-9 rounded-lg ${card.iconBg} flex items-center justify-center`}>
                          <i className={`${card.icon} ${card.textColor} text-base`}></i>
                        </div>
                        {card.trend !== 'stable' && (
                          <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                            card.trend === 'up' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'
                          }`}>
                            {card.trendValue}
                          </span>
                        )}
                      </div>
                      <div className={`text-lg md:text-xl font-bold ${card.textColor}`}>{card.value}</div>
                      <div className="text-[10px] text-foreground-400 mt-0.5">{card.label}</div>
                      <div className="text-[10px] text-foreground-500 mt-1">{card.subtitle}</div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* ─── SECCIÓN: BOTONES GRANDES DE ACCIÓN ─── */}
            {(sidebarView === 'todo') && (
              <div className="mb-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {dashboardQuickActions.map((action) => (
                    <a
                      key={action.id}
                      href={action.link}
                      className={`bg-gradient-to-r ${action.gradient} rounded-xl p-4 text-white group hover:scale-[1.03] transition-all cursor-pointer relative overflow-hidden`}
                    >
                      <div className="relative z-10">
                        <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center mb-3 group-hover:bg-white/30 transition-colors">
                          <i className={`${action.icon} text-lg`}></i>
                        </div>
                        <div className="text-sm font-bold whitespace-nowrap">{action.label}</div>
                        <div className="text-[10px] text-white/70 mt-1 leading-relaxed">{action.description}</div>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-r from-black/0 via-transparent to-black/0 group-hover:from-black/5 transition-colors"></div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* ─── SECCIÓN: ACTIVIDAD RECIENTE ─── */}
            {(sidebarView === 'todo' || sidebarView === 'actividad') && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <i className="ri-history-line text-teal-600 text-lg"></i>
                    <h2 className="text-lg font-bold text-foreground-950">Actividad Reciente</h2>
                  </div>
                  {sidebarView === 'todo' && (
                    <button
                      onClick={() => setSidebarView('actividad')}
                      className="text-xs text-emerald-600 font-medium hover:text-emerald-700 cursor-pointer"
                    >
                      Ver todo
                    </button>
                  )}
                </div>
                <div className="bg-background-100 rounded-xl border border-background-200/70 divide-y divide-background-200/70">
                  {dashboardRecentActivity.slice(0, sidebarView === 'todo' ? 5 : 8).map((act) => (
                    <a
                      key={act.id}
                      href={act.entityLink}
                      className="flex items-start gap-3 p-3.5 hover:bg-background-50 transition-colors cursor-pointer"
                    >
                      <div className={`w-9 h-9 rounded-lg ${act.bgColor} flex items-center justify-center shrink-0`}>
                        <i className={`${act.icon} ${act.color} text-sm`}></i>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-foreground-950">
                          <span className="font-semibold">{act.entity}</span>
                          <span className="text-foreground-500"> {act.message}</span>
                        </div>
                        <div className="text-[10px] text-foreground-400 mt-0.5">{act.timeAgo}</div>
                      </div>
                      <i className="ri-arrow-right-s-line text-foreground-300 text-sm shrink-0 mt-1"></i>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* ─── SECCIÓN: TAREAS RECOMENDADAS ─── */}
            {(sidebarView === 'todo' || sidebarView === 'tareas') && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <i className="ri-task-line text-amber-600 text-lg"></i>
                    <h2 className="text-lg font-bold text-foreground-950">Tareas Recomendadas</h2>
                    <span className="text-[10px] text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">
                      {taskCounts.total} pendientes
                    </span>
                  </div>
                  {sidebarView === 'todo' && (
                    <button
                      onClick={() => setSidebarView('tareas')}
                      className="text-xs text-emerald-600 font-medium hover:text-emerald-700 cursor-pointer"
                    >
                      Ver todo
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {dashboardTasks.slice(0, sidebarView === 'todo' ? 4 : 7).map((task) => (
                    <div
                      key={task.id}
                      className={`bg-background-100 rounded-xl p-5 border transition-colors ${
                        task.priority === 'alta'
                          ? 'border-rose-200 hover:border-rose-300'
                          : task.priority === 'media'
                          ? 'border-amber-200 hover:border-amber-300'
                          : 'border-teal-200 hover:border-teal-300'
                      }`}
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-lg ${
                          task.priority === 'alta' ? 'bg-rose-100' :
                          task.priority === 'media' ? 'bg-amber-100' :
                          'bg-teal-100'
                        } flex items-center justify-center shrink-0`}>
                          <i className={`${task.icon} ${
                            task.priority === 'alta' ? 'text-rose-600' :
                            task.priority === 'media' ? 'text-amber-600' :
                            'text-teal-600'
                          } text-sm`}></i>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-semibold text-sm text-foreground-950">{task.title}</span>
                            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                              task.priority === 'alta' ? 'bg-rose-100 text-rose-600' :
                              task.priority === 'media' ? 'bg-amber-100 text-amber-600' :
                              'bg-teal-100 text-teal-600'
                            }`}>
                              {task.priority.toUpperCase()}
                            </span>
                            <span className="text-[9px] text-foreground-300 px-1.5 py-0.5 rounded-full bg-background-200/50">
                              {task.category}
                            </span>
                          </div>
                          <p className="text-xs text-foreground-500 leading-relaxed mb-3">{task.description}</p>
                          <a
                            href={task.actionLink}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 transition-colors cursor-pointer"
                          >
                            {task.actionLabel}
                            <i className="ri-arrow-right-line text-xs"></i>
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ─── SECCIÓN: REGLAS ─── */}
            {(sidebarView === 'todo' || sidebarView === 'reglas') && (
              <div>
                {sidebarView === 'reglas' && (
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-scales-line text-violet-600 text-lg"></i>
                    <h2 className="text-lg font-bold text-foreground-950">Reglas del Dashboard</h2>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {dashboardRules.map((rule) => (
                    <div
                      key={rule.id}
                      className="bg-background-100 rounded-xl p-5 border border-background-200/70 hover:border-emerald-200 transition-colors text-center"
                    >
                      <div className={`w-12 h-12 rounded-xl bg-${rule.color}-50 flex items-center justify-center mx-auto mb-4`}>
                        <i className={`${rule.icon} text-xl text-${rule.color}-500`}></i>
                      </div>
                      <div className="font-semibold text-sm text-foreground-950 mb-2">{rule.title}</div>
                      <p className="text-xs text-foreground-500 leading-relaxed">{rule.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ─── BANNER FINAL ─── */}
            {sidebarView === 'todo' && (
              <div className="mt-8 bg-gradient-to-r from-emerald-500 via-teal-500 to-amber-500 rounded-xl p-5 text-white text-center">
                <i className="ri-eye-line text-2xl mb-2 block"></i>
                <div className="font-bold text-sm mb-1">Todo lo importante visible sin navegar</div>
                <p className="text-xs text-white/70">No necesitás abrir menús. No necesitás buscar. Todo está acá, en una sola pantalla.</p>
              </div>
            )}

            {/* ─── CROSS-LINKS (solo vista completa) ─── */}
            {sidebarView === 'todo' && (
              <div className="mt-8 border-t border-background-200/70 pt-6">
                <div className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">
                  Accesos directos
                </div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {[
                    { label: 'Analytics', icon: 'ri-bar-chart-2-line', link: '/admin/analytics' },
                    { label: 'Programación', icon: 'ri-calendar-line', link: '/admin/schedule' },
                    { label: 'Locutores', icon: 'ri-mic-line', link: '/admin/hosts' },
                    { label: 'Fotos', icon: 'ri-image-line', link: '/admin/photos' },
                    { label: 'Videos', icon: 'ri-video-line', link: '/admin/videos' },
                    { label: 'Soporte', icon: 'ri-customer-service-line', link: '/admin/support' },
                  ].map((link) => (
                    <a
                      key={link.label}
                      href={link.link}
                      className="bg-background-100 rounded-lg p-3 border border-background-200/70 text-center hover:border-emerald-200 transition-colors cursor-pointer"
                    >
                      <i className={`${link.icon} text-foreground-500 text-sm mb-1 block`}></i>
                      <span className="text-[10px] text-foreground-600">{link.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}